import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { UserProfile, Version } from '../types/common';
import { loadFromLocalStorage, saveToLocalStorage, debounce } from '../utils/helpers';
import { STORAGE_KEYS, DEFAULT_IDS } from '../constants';
import { migrateAllProfileIds } from '../utils/profileManager';
import { THEME_DARK_ENABLED } from '../styles/theme';
import {
  DEFAULT_THEME_PREFERENCE,
  SYSTEM_DARK_QUERY,
  parseStoredThemePreference,
  resolveTheme,
  systemPrefersDark,
  type ResolvedTheme,
  type ThemePreference,
} from '../styles/themePreference';

// Define the shape of our context
interface AppContextType {
  // User profile
  userProfile: UserProfile;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;

  // Versions
  versions: Version[];
  selectedVersion: string;
  setVersions: React.Dispatch<React.SetStateAction<Version[]>>;
  setSelectedVersion: React.Dispatch<React.SetStateAction<string>>;

  // UI state
  expandedSections: Record<string, boolean>;
  setExpandedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

  // Theme (design-system PR-1, 2026-09-23). The preference is what the person
  // chose (light / dark / system); the resolved theme is what <html data-theme>
  // carries and the semantic --color-* tokens read. `darkMode` / `setDarkMode`
  // stay for any existing reader, derived from the same state.
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
  resolvedTheme: ResolvedTheme;
  darkMode: boolean;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create the context with a default undefined value
const AppContext = createContext<AppContextType | undefined>(undefined);

// Define props for our provider component
interface AppProviderProps {
  children: ReactNode;
}

// Create the provider component
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // User profile state
  const [userProfile, setUserProfile] = useState<UserProfile>(() => {
    const storedProfile = loadFromLocalStorage(STORAGE_KEYS.USER_PROFILE, {
      memberId: DEFAULT_IDS.MEMBER_ID,
      accountId: DEFAULT_IDS.ACCOUNT_ID,
      username: '',
      email: '',
      fullName: '',
      phone: '',
      company: '',
      linkedin: ''
    });

    // Always ensure sensitive data is anonymized
    return {
      ...storedProfile,
      memberId: DEFAULT_IDS.MEMBER_ID, // Use anonymized member ID
      accountId: DEFAULT_IDS.ACCOUNT_ID, // Use anonymized account ID
      email: '' // Don't expose email
    };
  });

  // Version control state
  const [versions, setVersions] = useState<Version[]>(() =>
    loadFromLocalStorage(STORAGE_KEYS.VERSIONS, [])
  );
  const [selectedVersion, setSelectedVersion] = useState<string>(() =>
    loadFromLocalStorage(STORAGE_KEYS.SELECTED_VERSION, 'current')
  );

  // UI state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    loadFromLocalStorage(STORAGE_KEYS.EXPANDED_SECTIONS, {})
  );

  // Theme state (design-system PR-1, 2026-09-23). The stored `ui_theme` is read
  // RAW rather than through loadFromLocalStorage: it may be the legacy JSON
  // boolean this provider wrote for months, or the JSON string it writes now,
  // and parseStoredThemePreference is the ONE table that reads both — the
  // same table the pre-paint script in public/index.html mirrors, held in
  // lockstep by tests/unit/themeMechanism.test.ts.
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => {
    try {
      return parseStoredThemePreference(localStorage.getItem(STORAGE_KEYS.THEME));
    } catch (_e) {
      return DEFAULT_THEME_PREFERENCE;
    }
  });
  const [osDark, setOsDark] = useState<boolean>(() =>
    systemPrefersDark(typeof window !== 'undefined' ? window : undefined)
  );
  // THEME_DARK_ENABLED = false is the one-line rollback: every preference
  // resolves to light and the toggle becomes inert, with no data repair.
  const resolvedTheme: ResolvedTheme = THEME_DARK_ENABLED ? resolveTheme(themePreference, osDark) : 'light';
  const darkMode = resolvedTheme === 'dark';

  const setThemePreference = useCallback((preference: ThemePreference) => {
    setThemePreferenceState(preference);
  }, []);

  // The legacy boolean setter, kept for any existing reader: true is dark, false is light.
  const setDarkMode = useCallback<React.Dispatch<React.SetStateAction<boolean>>>((next) => {
    setThemePreferenceState((prev) => {
      const prevDark = resolveTheme(prev, osDark) === 'dark';
      const value = typeof next === 'function' ? next(prevDark) : next;
      return value ? 'dark' : 'light';
    });
  }, [osDark]);

  // 'system' follows the device: subscribe to the media query while mounted.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(SYSTEM_DARK_QUERY);
    } catch (_e) {
      return undefined;
    }
    const onChange = (event: MediaQueryListEvent) => setOsDark(event.matches);
    setOsDark(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, []);

  // Create debounced save function
  const debouncedSaveUserProfile = useCallback(
    (data: UserProfile) => {
      const debouncedFn = debounce((profileData: UserProfile) => {
        saveToLocalStorage(STORAGE_KEYS.USER_PROFILE, profileData);
      }, 500);
      debouncedFn(data);
    },
    []
  );

  // Save state to localStorage whenever it changes
  useEffect(() => {
    debouncedSaveUserProfile(userProfile);
  }, [userProfile, debouncedSaveUserProfile]);

  // Migrate all profiles to ensure they have 9-digit account IDs
  useEffect(() => {
    // Run once on app initialization
    console.log('Checking and migrating profiles to 9-digit account and member IDs if needed...');
    migrateAllProfileIds();

    // Also check the current user profile
    if (userProfile && userProfile.accountId) {
      let needsUpdate = false;
      let updatedUserProfile = {...userProfile};

      // Check if the current user account ID is valid (9 digits)
      const isValidAccountId = /^[1-9]\d{8}$/.test(userProfile.accountId);

      if (!isValidAccountId) {
        console.log(`Current user profile needs account ID migration: ${userProfile.accountId}`);

        // Generate a 9-digit account ID
        const firstDigit = Math.floor(Math.random() * 9) + 1;
        const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
        const newAccountId = `${firstDigit}${remainingDigits}`;

        updatedUserProfile.accountId = newAccountId;
        needsUpdate = true;

        console.log(`Migrated current user account ID: ${userProfile.accountId} → ${newAccountId}`);
      }

      // Check if the current user member ID is valid (9 digits)
      const isValidMemberId = /^[1-9]\d{8}$/.test(userProfile.memberId);

      if (!isValidMemberId) {
        console.log(`Current user profile needs member ID migration: ${userProfile.memberId}`);

        // Generate a 9-digit member ID
        const firstDigit = Math.floor(Math.random() * 9) + 1;
        const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
        const newMemberId = `${firstDigit}${remainingDigits}`;

        updatedUserProfile.memberId = newMemberId;
        needsUpdate = true;

        console.log(`Migrated current user member ID: ${userProfile.memberId} → ${newMemberId}`);
      }

      // Update the user profile if needed
      if (needsUpdate) {
        setUserProfile(updatedUserProfile);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.VERSIONS, versions);
  }, [versions]);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.SELECTED_VERSION, selectedVersion);
  }, [selectedVersion]);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.EXPANDED_SECTIONS, expandedSections);
  }, [expandedSections]);

  // Persisted on the SAME `ui_theme` key the legacy boolean used — a stored
  // `true` / `false` is migrated on read and rewritten as the string here.
  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.THEME, themePreference);
  }, [themePreference]);

  // What the tokens read: <html data-theme="light|dark">. The pre-paint script
  // in public/index.html set the same value before the bundle ran; this keeps
  // it true whenever the preference or the device's scheme changes.
  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-theme', resolvedTheme);
    } catch (_e) {
      /* no document (tests, SSR) — nothing reads the attribute there */
    }
  }, [resolvedTheme]);

  // Create the context value object
  const contextValue = {
    userProfile,
    setUserProfile,
    versions,
    setVersions,
    selectedVersion,
    setSelectedVersion,
    expandedSections,
    setExpandedSections,
    themePreference,
    setThemePreference,
    resolvedTheme,
    darkMode,
    setDarkMode
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Create a hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
